import { useEffect, useState } from "react";
import { ApiError, apiRequest } from "./api";
import { ChangePasswordPage, LoginPage } from "./pages/AuthPages";
import type { ImportLinks } from "../src/contracts";

interface SessionResponse {
  initialized: boolean;
  authenticated: boolean;
  mustChangePassword: boolean;
  csrfToken?: string;
}

interface InitialSecret {
  readToken: string;
  importLinks: ImportLinks;
}

export default function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [initialSecret, setInitialSecret] = useState<InitialSecret | null>(null);
  const [startupError, setStartupError] = useState("");

  useEffect(() => {
    let active = true;
    apiRequest<SessionResponse>("/api/session")
      .then((value) => {
        if (active) setSession(value);
      })
      .catch((error) => {
        if (active) {
          setStartupError(error instanceof ApiError ? error.message : "无法连接 iOSIP");
          setSession({ initialized: true, authenticated: false, mustChangePassword: false });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  if (!session) {
    return (
      <main className="app-loading" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <span>正在连接…</span>
      </main>
    );
  }

  if (!session.authenticated) {
    return (
      <>
        <LoginPage
          onSuccess={(result) => {
            setStartupError("");
            setSession({ initialized: true, ...result });
          }}
        />
        {startupError ? (
          <div className="startup-banner" role="alert">
            {startupError}
          </div>
        ) : null}
      </>
    );
  }

  if (session.mustChangePassword) {
    return (
      <ChangePasswordPage
        csrfToken={session.csrfToken ?? ""}
        onSuccess={(result) => {
          setSession({ initialized: true, ...result });
          setInitialSecret({ readToken: result.readToken, importLinks: result.importLinks });
        }}
      />
    );
  }

  return (
    <ManagerLoader
      csrfToken={session.csrfToken ?? ""}
      initialSecret={initialSecret}
      onSessionExpired={() => {
        setInitialSecret(null);
        setSession({ initialized: true, authenticated: false, mustChangePassword: false });
      }}
    />
  );
}

function ManagerLoader({
  csrfToken,
  initialSecret,
  onSessionExpired
}: {
  csrfToken: string;
  initialSecret: InitialSecret | null;
  onSessionExpired: () => void;
}) {
  const [Manager, setManager] = useState<React.ComponentType<{
    csrfToken: string;
    initialSecret: InitialSecret | null;
    onSessionExpired: () => void;
  }> | null>(null);

  useEffect(() => {
    import("./pages/ManagerPage").then((module) => setManager(() => module.ManagerPage));
  }, []);

  if (!Manager) {
    return (
      <main className="app-loading" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <span>正在载入管理页面…</span>
      </main>
    );
  }
  return (
    <Manager
      csrfToken={csrfToken}
      initialSecret={initialSecret}
      onSessionExpired={onSessionExpired}
    />
  );
}
